"use client";

import { c as _c } from "react/compiler-runtime";
import { locale } from "@locale";
import classNames from "classnames";
import type { SanityReference } from "next-sanity";
import { type RefObject } from "react";
import CurrentFilter from "src/components/filterselector/CurrentFilter";
import LocationSelectorButton from "src/components/locationselector/LocationSelectorButton";
import { MySelectedLocationsList } from "src/components/locationselector/MySelectedLocations";
import ProgramDateButtonRow from "src/components/programdatebutton/ProgramDateButtonRow";
import ProgramControlsSponsor from "src/components/takoversponsor/ProgramControlsSponsor";
import useElementInView from "src/lib/client/useElementInView";
import useScrollToTopOnChange from "src/lib/client/useScrollToTopOnChange";
import { useLocationContext, useProgramDatesContext } from "src/lib/contexts/ContextTypes";
import { formatLocaleString } from "src/lib/locale/helpers";
import styles from './ProgramControls.module.css';

//#region [Props]
type ProgramControlsProps = {
  hasSponsor?: boolean;
  sponsorClickUrl?: string | null;
  sponsorTopImageDesktop?: SanityReference[] | null;
  sponsorTopImageMobile?: SanityReference[] | null;
  hasSponsorBg?: boolean;
  scrollToRef?: RefObject<HTMLElement | null>;
  predefinedLocation?: string;
  predefinedCinema?: string;
  disableLocationSelector?: boolean;
  disableFilter?: boolean;
  disableDateSelector?: boolean;
};
//#endregion

// 👇 juster verdien til ca. høyden på den LAVE sticky-headeren (f.eks. 64–96px)
const STICKY_BUFFER_PX = 80;

//#region [Component]
export default function ProgramControls(t0) {
  const $ = _c(64);
  const {
    hasSponsor,
    sponsorClickUrl,
    sponsorTopImageDesktop,
    sponsorTopImageMobile,
    hasSponsorBg,
    scrollToRef,
    predefinedLocation,
    predefinedCinema,
    disableLocationSelector,
    disableFilter,
    disableDateSelector: t1
  } = t0;
  t1 === undefined ? false : t1;
  const locationContext = useLocationContext();
  const selectedDateContext = useProgramDatesContext();
  const date = selectedDateContext.selectedDate;
  let t2;
  if ($[0] !== date) {
    t2 = [date];
    $[0] = date;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== scrollToRef) {
    t3 = {
      behavior: "smooth",
      elementRef: scrollToRef
    };
    $[2] = scrollToRef;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useScrollToTopOnChange(t2, t3, !!locationContext.isReady);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      threshold: 0,
      rootMargin: `-${STICKY_BUFFER_PX}px 0px 0px 0px`
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    ref: sentinelRef,
    isInView
  } = useElementInView(t4);
  const isSticky = !isInView;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      height: 1
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== sentinelRef) {
    t6 = <div ref={sentinelRef} aria-hidden={true} style={t5} />;
    $[6] = sentinelRef;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== hasSponsorBg || $[9] !== isSticky) {
    t7 = classNames(styles.controlsContainer, {
      [styles.isSticky]: isSticky,
      [styles.hasSponsorBg]: hasSponsorBg
    });
    $[8] = hasSponsorBg;
    $[9] = isSticky;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== hasSponsor || $[12] !== isSticky || $[13] !== sponsorClickUrl || $[14] !== sponsorTopImageDesktop || $[15] !== sponsorTopImageMobile) {
    t8 = hasSponsor && <ProgramControlsSponsor isSticky={isSticky} className={classNames(styles.programControlsSponsor, {
      [styles.isSticky]: isSticky
    })} sponsorClickUrl={sponsorClickUrl} sponsorTopImageDesktop={sponsorTopImageDesktop} sponsorTopImageMobile={sponsorTopImageMobile} />;
    $[11] = hasSponsor;
    $[12] = isSticky;
    $[13] = sponsorClickUrl;
    $[14] = sponsorTopImageDesktop;
    $[15] = sponsorTopImageMobile;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== isSticky) {
    t9 = classNames(styles.userControls, {
      [styles.isSticky]: isSticky
    });
    $[17] = isSticky;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = !!predefinedLocation;
  let t11;
  if ($[19] !== t10) {
    t11 = classNames(styles.headerContainer, {
      [styles.predefinedLocation]: t10
    });
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = !!predefinedLocation;
  let t13;
  if ($[21] !== hasSponsor || $[22] !== isSticky || $[23] !== t12) {
    t13 = classNames(styles.title, {
      [styles.isSticky]: isSticky,
      [styles.hasSponsor]: hasSponsor,
      [styles.predefinedLocation]: t12
    });
    $[21] = hasSponsor;
    $[22] = isSticky;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== predefinedLocation) {
    t14 = !predefinedLocation && <>{locale.program.pageTitle}</>;
    $[25] = predefinedLocation;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== predefinedCinema || $[28] !== predefinedLocation) {
    t15 = !!predefinedLocation && <>{!predefinedCinema && <>{formatLocaleString(locale.program.pageTitleLocation, {
          location: predefinedLocation
        })}</>}{predefinedCinema && <>{formatLocaleString(locale.program.pageTitleCinema, {
          location: predefinedLocation,
          cinema: predefinedCinema
        })}</>}</>;
    $[27] = predefinedCinema;
    $[28] = predefinedLocation;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t13 || $[31] !== t14 || $[32] !== t15) {
    t16 = <h1 className={t13}>{t14}{t15}</h1>;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== predefinedCinema || $[35] !== predefinedLocation) {
    t17 = !!predefinedLocation && <>{!predefinedCinema && <p className={styles.subTitle}>{formatLocaleString(locale.program.pageSubtitleLocation, {
          location: predefinedLocation
        })}</p>}{!!predefinedCinema && <p className={styles.subTitle}>{formatLocaleString(locale.program.pageSubtitleCinema, {
          location: predefinedLocation,
          cinema: predefinedCinema
        })}</p>}</>;
    $[34] = predefinedCinema;
    $[35] = predefinedLocation;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t11 || $[38] !== t16 || $[39] !== t17) {
    t18 = <header className={t11}>{t16}{t17}</header>;
    $[37] = t11;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== disableLocationSelector || $[42] !== isSticky) {
    t19 = !disableLocationSelector && <MySelectedLocationsList className={classNames(styles.mySelectedLocations, {
      [styles.isSticky]: isSticky
    })}><LocationSelectorButton className={classNames(styles.locationSelectorButton, {
        [styles.isSticky]: isSticky
      })} showTextLabel={true} /></MySelectedLocationsList>;
    $[41] = disableLocationSelector;
    $[42] = isSticky;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== selectedDateContext.availableDates || $[45] !== selectedDateContext.selectedDate || $[46] !== selectedDateContext.setSelectedDate || $[47] !== selectedDateContext.showingAllDates) {
    t20 = <ProgramDateButtonRow dates={selectedDateContext.availableDates} selectedDate={selectedDateContext.selectedDate} isAllSelected={selectedDateContext.showingAllDates} className={styles.dateRow} onButtonClick={selectedDateContext.setSelectedDate} />;
    $[44] = selectedDateContext.availableDates;
    $[45] = selectedDateContext.selectedDate;
    $[46] = selectedDateContext.setSelectedDate;
    $[47] = selectedDateContext.showingAllDates;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t18 || $[50] !== t19 || $[51] !== t20 || $[52] !== t9) {
    t21 = <div className={t9}>{t18}{t19}{t20}</div>;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
    $[52] = t9;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t21 || $[55] !== t7 || $[56] !== t8) {
    t22 = <div className={t7}>{t8}{t21}</div>;
    $[54] = t21;
    $[55] = t7;
    $[56] = t8;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== disableFilter) {
    t23 = !disableFilter && <div className={styles.filterSelectors}><CurrentFilter /></div>;
    $[58] = disableFilter;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== t22 || $[61] !== t23 || $[62] !== t6) {
    t24 = <>{t6}{t22}{t23}</>;
    $[60] = t22;
    $[61] = t23;
    $[62] = t6;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  return t24;
}
//#endregion